import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import {
  CATALOG_ROWS,
  defaultUnitPrice,
  unitPriceFor,
  type ContractSection,
  type ContractUnitPrices,
} from '../../utils/contractPricing';
import { catalogRowSuffix } from '../../utils/serviceAgreementLayout';
import './ContractPriceModal.css';

interface ContractPriceModalProps {
  isOpen: boolean;
  /** Which catalog list is being repriced. */
  section: ContractSection;
  /** The contract's current overrides. */
  unitPrices: ContractUnitPrices | undefined;
  onCancel: () => void;
  /** Receives the full override map for the section, already pruned of defaults. */
  onSave: (unitPrices: ContractUnitPrices) => void;
}

const SECTION_LABEL: Record<ContractSection, string> = {
  monthly: 'Robot Quantity, Model and Monthly Cost per Item',
  accessory: 'Additional Accessories',
};

const SECTION_HINT: Record<ContractSection, string> = {
  monthly: 'Monthly price per unit.',
  accessory: 'One-time price per unit.',
};

/**
 * Reprice the catalog lines for one contract.
 *
 * Prices apply to this contract only — the catalog defaults in
 * `serviceAgreementLayout.mjs` are untouched, so the next contract starts from
 * the standard price again. Whatever is set here is what prints on the PDF.
 */
const ContractPriceModal: React.FC<ContractPriceModalProps> = ({
  isOpen,
  section,
  unitPrices,
  onCancel,
  onSave,
}) => {
  const rows = CATALOG_ROWS[section];
  /** Kept as strings so a half-typed value like "1." survives a re-render. */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const initialDrafts = useMemo(() => {
    const next: Record<string, string> = {};
    for (const row of rows) next[row.key] = String(unitPriceFor(row.key, unitPrices));
    return next;
  }, [rows, unitPrices]);

  useEffect(() => {
    if (!isOpen) return;
    setDrafts(initialDrafts);
    setError('');
  }, [isOpen, initialDrafts]);

  const handleSave = () => {
    const next: ContractUnitPrices = { ...unitPrices };
    for (const row of rows) {
      const raw = String(drafts[row.key] ?? '').replace(/[$,\s]/g, '');
      const amount = raw === '' ? 0 : Number(raw);
      if (!Number.isFinite(amount) || amount < 0) {
        setError(`Enter a valid price for ${row.label}.`);
        return;
      }
      // Only real negotiations are stored; a line back at its catalog price
      // drops out so it keeps tracking the default.
      if (amount === defaultUnitPrice(row.key)) delete next[row.key];
      else next[row.key] = amount;
    }
    setError('');
    onSave(next);
  };

  const resetAll = () => {
    const next: Record<string, string> = {};
    for (const row of rows) next[row.key] = String(defaultUnitPrice(row.key));
    setDrafts(next);
    setError('');
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="Edit Prices" elevated wide>
      <div className="modal-body contract-price-modal">
        <p className="contract-price-modal-section">
          Editing <strong>{SECTION_LABEL[section]}</strong>
        </p>
        <p className="contract-price-modal-desc">
          {SECTION_HINT[section]} These prices apply to this contract only and are what print on the
          agreement. Leaving a price at its standard value keeps it tracking the catalog.
        </p>

        <table className="contract-price-table">
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col">Standard</th>
              <th scope="col">This contract ($)</th>
              <th scope="col">Prints as</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const raw = String(drafts[row.key] ?? '').replace(/[$,\s]/g, '');
              const amount = raw === '' ? 0 : Number(raw);
              const valid = Number.isFinite(amount) && amount >= 0;
              const changed = valid && amount !== defaultUnitPrice(row.key);
              return (
                <tr key={row.key} className={changed ? 'contract-price-row--changed' : undefined}>
                  <th scope="row">{row.label}</th>
                  <td className="contract-price-default">
                    ${defaultUnitPrice(row.key).toLocaleString()}
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-input contract-price-input"
                      value={drafts[row.key] ?? ''}
                      onChange={(e) => {
                        setDrafts((prev) => ({ ...prev, [row.key]: e.target.value }));
                        setError('');
                      }}
                      aria-label={`Price for ${row.label}`}
                    />
                  </td>
                  <td className="contract-price-preview">
                    {valid ? catalogRowSuffix(row, amount) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {error && <p className="contract-price-modal-error">{error}</p>}
      </div>

      <div className="modal-actions contract-price-modal-actions">
        <button type="button" className="contract-price-reset" onClick={resetAll}>
          Reset to standard prices
        </button>
        <button type="button" className="cancel-button" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="generate-link-button" onClick={handleSave}>
          Save Prices
        </button>
      </div>
    </Modal>
  );
};

export default ContractPriceModal;
